import { type FC, useState } from "react";
import { useApolloClient } from "@apollo/client";
import AppBar from "@mui/material/AppBar";
import TablePagination from "@mui/material/TablePagination";
import { Box, CircularProgress, Stack } from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from "@hello-pangea/dnd";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  PlatformMenuItemsListQuery,
  usePlatformMenuItemsQuery,
  useUpdatePlatformMenuItemMutation,
} from "@/partner/entities/platformMenuItem";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { PlatformMenuListItem } from "./PlatformMenuListItem";
import { PlatformMenuListToolbar } from "./PlatformMenuListToolbar";
import type { PlatformMenuItemsListQueryType } from "@/types/graphql/partner.generated";

interface PlatformMenuListProps {
  onEditItem: (id: string) => void;
  onCreateItem: (index: number) => void;
}

export const PlatformMenuList: FC<PlatformMenuListProps> = ({
  onEditItem,
  onCreateItem,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { cache } = useApolloClient();

  const {
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
    filter: { search },
  } = useFilter();

  const listVariables = {
    page,
    rowsPerPage,
    filter: {
      search,
    },
  };

  const { data, loading } = usePlatformMenuItemsQuery({
    fetchPolicy: "cache-and-network",
    variables: listVariables,
  });

  const [updatePlatformMenuItem] = useUpdatePlatformMenuItemMutation({
    onCompleted: () => {
      enqueueSnackbar(t("platformMenu.snackbar.reordered"), {
        variant: "success",
      });
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
    refetchQueries: ["ArenaMenuItemsList"],
  });

  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const selectedIds = selectedItems.map((c) => c.id);

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          py: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const nodes = data?.platformMenuItems.nodes || [];
  const count = data?.platformMenuItems.count || 0;

  if (count === 0) {
    return <EmptyListCard title={t("platformMenu.list.empty")} />;
  }

  const numSelected = selectedIds.length;
  const indeterminate = numSelected > 0 && numSelected < nodes.length;

  function onSelectAllClick() {
    if (numSelected === nodes.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(nodes.map((item) => ({ id: item.id })));
    }
  }

  const handleCloseSelected = () => {
    setSelectedItems([]);
  };

  const onSelectOneChange = (id: string) => {
    setSelectedItems((selected) => {
      if (selectedIds.includes(id)) {
        return selected.filter((c) => c.id !== id);
      }
      return selected.concat({ id });
    });
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    const targetItemIndex = nodes[destinationIndex].index;
    const item = nodes[sourceIndex];

    const menuItems = cache.readQuery<PlatformMenuItemsListQueryType>({
      query: PlatformMenuItemsListQuery,
      variables: listVariables,
    });

    if (!menuItems) return;

    const updatedNodes = [...nodes];
    const [movedItem] = updatedNodes.splice(sourceIndex, 1);
    updatedNodes.splice(destinationIndex, 0, movedItem);

    cache.writeQuery({
      query: PlatformMenuItemsListQuery,
      variables: listVariables,
      data: {
        platformMenuItems: {
          ...menuItems.platformMenuItems,
          nodes: updatedNodes,
        },
      },
    });

    updatePlatformMenuItem({
      variables: {
        id: item.id,
        input: {
          index: targetItemIndex,
        },
      },
    });
  };

  return (
    <>
      <AppBar position="static" color="default" elevation={0}>
        <PlatformMenuListToolbar
          indeterminate={indeterminate}
          numSelected={numSelected}
          nodes={nodes}
          onSelectAllClick={onSelectAllClick}
          selectedItems={selectedItems}
          onCloseSelected={handleCloseSelected}
        />
      </AppBar>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="platform-menu-list">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <Stack spacing={1}>
                {nodes.map((item, index) => {
                  const selected = selectedIds.includes(item.id);
                  return (
                    <Draggable
                      key={item.id}
                      draggableId={item.id}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1,
                          }}
                        >
                          <PlatformMenuListItem
                            item={item}
                            index={index}
                            selected={selected}
                            onSelectOneChange={onSelectOneChange}
                            onEditItem={onEditItem}
                            onCreateItem={onCreateItem}
                            isLast={index === nodes.length - 1}
                            dragHandleProps={provided.dragHandleProps}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              </Stack>
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <TablePagination
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </>
  );
};
